import { Generations } from '@smogon/calc';
import { COMMON_SETS } from '../data/commonSets';

// Cache for Generations objects
const genCache = {};

/**
 * Get a @smogon/calc Generations object for a given gen number (1–9).
 */
export function getGen(genNum) {
  if (!genCache[genNum]) {
    genCache[genNum] = Generations.get(genNum);
  }
  return genCache[genNum];
}

// Known multi-word Pokémon names that use spaces in Showdown format
// (vs hyphenated formes like Urshifu-Rapid-Strike)
const SPACE_NAMES = new Set([
  'flutter mane', 'iron hands', 'iron bundle', 'iron valiant', 'iron moth',
  'iron jugulis', 'iron thorns', 'iron treads', 'iron leaves', 'iron boulder',
  'iron crown', 'great tusk', 'brute bonnet', 'sandy shocks', 'scream tail',
  'slither wing', 'walking wake', 'roaring moon', 'chi yu',
  'chien pao', 'ting lu', 'wo chien', 'gouging fire', 'raging bolt',
  'ho oh', 'mr mime', 'mr rime', 'mime jr', 'porygon z',
  'jangmo o', 'hakamo o', 'kommo o',
  'tapu koko', 'tapu lele', 'tapu bulu', 'tapu fini',
  'type null',
]);

// PokeAPI stores some form names that differ from Showdown's species identifier.
// These overrides map the PokeAPI key → the exact string @smogon/calc expects.
const SHOWDOWN_NAME_OVERRIDES = {
  'tornadus-incarnate':       'Tornadus',
  'thundurus-incarnate':      'Thundurus',
  'landorus-incarnate':       'Landorus',
  'enamorus-incarnate':       'Enamorus',
  'giratina-altered':         'Giratina',
  'darmanitan-standard':      'Darmanitan',
  'darmanitan-galar-standard':'Darmanitan-Galar',
  'deoxys-normal':            'Deoxys-Normal',
  'shaymin-land':             'Shaymin',
  'keldeo-ordinary':          'Keldeo',
  'meloetta-aria':            'Meloetta',
  'meowstic-male':            'Meowstic',
  'meowstic-female':          'Meowstic-F',
  'aegislash-shield':         'Aegislash',
  'wishiwashi-solo':          'Wishiwashi',
  'wormadam-plant':           'Wormadam',
  'necrozma-dusk':            'Necrozma-Dusk-Mane',
  'necrozma-dawn':            'Necrozma-Dawn-Wings',
  'oricorio-pau':             'Oricorio-Pa',
  'maushold-family-of-four':  'Maushold-Four',
  'squawkabilly-blue-plumage':   'Squawkabilly-Blue',
  'squawkabilly-yellow-plumage': 'Squawkabilly-Yellow',
  'squawkabilly-white-plumage':  'Squawkabilly-White',
  'tatsugiri-droopy':         'Tatsugiri',
  'tatsugiri-stretchy':       'Tatsugiri',
  'oinkologne-female':        'Oinkologne-F',
};

/**
 * Convert a backend Pokémon name (e.g. "flutter-mane", "urshifu-rapid-strike")
 * to a Showdown-format name (e.g. "Flutter Mane", "Urshifu-Rapid-Strike").
 *
 * Strategy:
 * 1. Check SHOWDOWN_NAME_OVERRIDES for PokeAPI form names that differ from Showdown.
 * 2. Check COMMON_SETS for an explicit showdownName mapping.
 * 3. Otherwise, title-case each hyphenated segment.
 *    - If the lowercase joined-by-space version is in SPACE_NAMES → join with spaces.
 *    - Otherwise → join with hyphens (i.e. it's a forme).
 */
export function toShowdownName(name) {
  if (!name) return '';

  const key = name.toLowerCase().replace(/\s+/g, '-');

  // Check PokeAPI form → Showdown name overrides first
  if (SHOWDOWN_NAME_OVERRIDES[key]) return SHOWDOWN_NAME_OVERRIDES[key];

  // Check explicit COMMON_SETS mapping
  if (COMMON_SETS[key]) return COMMON_SETS[key].showdownName;

  const parts = name.toLowerCase().split('-');
  const titleParts = parts.map(p => p.charAt(0).toUpperCase() + p.slice(1));
  const spacedLower = parts.join(' ');

  if (SPACE_NAMES.has(spacedLower)) {
    return titleParts.join(' ');
  }

  // Default: join with hyphens (forme-style names)
  return titleParts.join('-');
}

const STAT_LABELS = { hp: 'HP', atk: 'Atk', def: 'Def', spa: 'SpA', spd: 'SpD', spe: 'Spe' };
export function statLabel(key) { return STAT_LABELS[key] || key; }
